// react, twin.macro, next
import dynamic from 'next/dynamic';

// helpers, constant, context
import dayjs from 'dayjs';

// apis
import catalogueAPI from 'api/interfaces/catalogue-interfaces';
import paymentAPI from 'api/interfaces/payment-interfaces';
import config from 'constant/config';

const utc = require('dayjs/plugin/utc');
const timezone = require('dayjs/plugin/timezone');
const isBetween = require('dayjs/plugin/isBetween');

dayjs.extend(isBetween);
dayjs.extend(utc);
dayjs.extend(timezone);

const LgPdpRebranding = dynamic(() => import(
  /* webpackChunkName: "product_detail_page_rebranding" */
  'components/Pages/LgProductDetailPage'
));

const LgPdpOld = dynamic(() => import(
  /* webpackChunkName: "product_detail_page_old" */
  'components/Pages/Product'
));

const LgProductSlugOld = dynamic(() => import(
  /* webpackChunkName: "product_slug_old" */
  'components/Pages/ProductSlug'
));


const LgPdpComponent = ({ useRebranding, isProductSlugPath, ...props }) => {
  if (useRebranding) return <LgPdpRebranding isProductSlugPath={isProductSlugPath} {...props} />;

  if (isProductSlugPath) return <LgProductSlugOld {...props} />
  return <LgPdpOld {...props} />
}

export async function getServerSideProps({ req, query, isGiftPath = false, isProductSlugPath = false }) {
  let games = [];
  let categoryCode = "";

  const { headers = {} } = req.locals || {};
  const { langCountry, slug, productSlug } = query;

  const currCountryFromSlug = (langCountry.split('-'))[1];
  const comingSoonCountry = config.pdpComingSoonCountrySlug.split(',');
  const isAnnouncementPhaseCountry = comingSoonCountry.includes(currCountryFromSlug);

  if (isGiftPath && isAnnouncementPhaseCountry) {
    return {
      redirect: {
        permanent: false,
        destination: `/${langCountry}/${slug}`,
      }
    }
  }

  const parallelFetches = [
    catalogueAPI.getActiveCategories({ headers }),
    paymentAPI.getAvailablePaymentMethod({ headers }),
    catalogueAPI.getBanners({ slug: `/${langCountry}/${slug}`, headers }),
  ];

  if (isProductSlugPath && productSlug) {
    parallelFetches.push(...[
      catalogueAPI.getProducts({ slug, headers, productSlug }),
      catalogueAPI.getCategories({ slug, productSlug, headers }),
      catalogueAPI.getSEO({ slug: `/${langCountry}/${slug}/${productSlug}`, headers }),
    ]);
  } else {
    parallelFetches.push(...[
      catalogueAPI.getProducts({ slug, headers }),
      catalogueAPI.getCategories({ slug, headers }),
      catalogueAPI.getSEO({ slug: `/${langCountry}/${slug}`, headers }),
    ]);
  }

  const [
    // general
    categorySectionsRes,
    availablePaymentMethodGroupsRes,
    bannerRes,

    // product specific
    productRes,
    ProductInfoRes,
    seoRes,
  ] = await Promise.all(parallelFetches);

  const [
    // general
    categorySections,
    availablePaymentMethodGroupsData,
    banner,

    // product specific
    products,
    productInfo,
    seo,
  ] = [
      // general
      categorySectionsRes?.data,
      availablePaymentMethodGroupsRes?.data,
      bannerRes?.data,

      // product specific
      productRes?.data,
      ProductInfoRes?.data,
      seoRes?.data,
    ];

  if (categorySections.success) {
    categorySections.data.map(item => item.categories.map(game => {
      if (game.slug === query.slug) {
        categoryCode = game.code;
      }
      games.push(game);
    }));
  }

  // for now this is not necessary, as we don't use cookie at all caused by varnish config that strips cookies and queries.
  if (req?.headers?.cookie) {
    const currCountry = req.headers.cookie.split(';')
    const filteredCookie = currCountry.filter(res => res.includes('currCountry='))

    if (filteredCookie?.length === 1) {
      const currCookieCountry = filteredCookie[0].split("=");
      const selectedPath = currCookieCountry[currCookieCountry.length - 1]
      const rgxLocale = /([a-z]{2}(-[a-zA-Z]{2})$)/

      if (query?.langCountry !== selectedPath && rgxLocale.test(selectedPath)) {
        return {
          redirect: {
            permanent: false,
            destination: `/${selectedPath}`
          }
        }
      }
    }
  }

  // Redirect to home
  if (!productInfo.success || (isProductSlugPath && !products.success)) {
    return {
      redirect: {
        destination: `/${langCountry}`,
        permanent: false,
      },
    };
  }

  if (availablePaymentMethodGroupsData.success && availablePaymentMethodGroupsData.data) {
    availablePaymentMethodGroupsData.data.forEach(paymentMethodGroupData => paymentMethodGroupData.paymentMethods = paymentMethodGroupData.paymentMethod);
  }

  if (products.success) {
    products?.data?.productsByCategories?.forEach(group => group?.products?.forEach(product => {
      product.productCode = product.code;
      product.productName = product.name;
    }))
  }

  const usingRebrandingUserAgent = req?.headers?.['x-rebranding'] === 'true';
  const useRebranding = (langCountry === 'id-id') ? (usingRebrandingUserAgent || config.isRebrandingProductDetailPageActive) : (usingRebrandingUserAgent || config.isRebrandingInternationalActive);

  return {
    props: {
      banner: banner && banner.success ? banner.data : [],
      banners: banner && banner.success ? banner.data : [],
      seo: seo.success ? seo.data : {},
      product: products.success ? products.data : {},
      productInfo: productInfo.success ? productInfo.data : {},
      categoryCode: categoryCode,
      payment: availablePaymentMethodGroupsData.success ? availablePaymentMethodGroupsData.data : [],
      availablePaymentMethodGroups: availablePaymentMethodGroupsData.success ? availablePaymentMethodGroupsData.data : [],
      useRebranding,
      isGiftPath,
      categorySections: categorySections?.success ? categorySections.data : [],
      games,
      isProductSlugPath,
    },
  };
}

export default LgPdpComponent;
